import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { IAppState } from './reducers';
import { ActionType as ValidationAction } from './validation';
import { Validation, ValidationSchema, SharedAssetExpiration } from '../constants/validation';
import { validationReducer } from '../utils/validation';
import { AssetHit } from '../api/vault/searchEngine/interfaces';

const SHARE_EMAIL_ADDRESSES_STORAGE_KEY = 'catapult:shareAssetsEmailAddresses';

export enum ValidationKey {
  Email = 'ShareEmailDialogInput',
}

export const ValidationRules = {
  Email: [Validation.NOT_EMPTY, Validation.EMAIL],
};

export enum ActionType {
  Toggle = 'ShareEmailDialog.Toggle',
  UpdateEmail = 'ShareEmailDialog.UpdateEmail',
  UpdateEmailNote = 'ShareEmailDialog.UpdateEmailNote',
  AddEmail = 'ShareEmailDialog.AddEmail',
  RemoveEmail = 'ShareEmailDialog.RemoveEmail',
  UpdateDaysToExpire = 'ShareEmailDialog.UpdateDaysToExpire',
  ClearAll = 'ShareEmailDialog.ClearAll',
}

export interface IEmailDialogAction extends Action<ActionType> {
  payload?: any;
}

export interface IState {
  isOpen: boolean;
  selectedAssets: Array<AssetHit>;
  email: string;
  emailNote: string;
  emailAddresses: Array<string>;
  cachedEmails: Array<string>;
  daysToExpire: number;
}

const getCachedEmailAddresses = (): Array<string> => {
  try {
    const stored = localStorage.getItem(SHARE_EMAIL_ADDRESSES_STORAGE_KEY);
    if (stored && stored !== 'undefined') {
      const previouslyUsedAddresses = JSON.parse(stored);
      if (previouslyUsedAddresses && previouslyUsedAddresses.length) {
        return previouslyUsedAddresses;
      }
    }
  } catch (e) {
    // Invalid JSON
  }
  return [];
};

const INITIAL_STATE: IState = {
  isOpen: false,
  selectedAssets: [],
  email: '',
  emailNote: '',
  emailAddresses: [],
  cachedEmails: getCachedEmailAddresses(),
  daysToExpire: SharedAssetExpiration.Default,
};

export default (state: IState = INITIAL_STATE, action: IEmailDialogAction) => {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
        selectedAssets: action.payload.selectedAssets,
      };
    case ActionType.UpdateEmail:
      return {
        ...state,
        email: action.payload.email,
      };
    case ActionType.UpdateEmailNote:
      return {
        ...state,
        emailNote: action.payload.emailNote,
      };
    case ActionType.AddEmail:
      return {
        ...state,
        emailAddresses: [
          ...state.emailAddresses,
          action.payload.email,
        ],
      };
    case ActionType.RemoveEmail:
      return {
        ...state,
        emailAddresses: state.emailAddresses
          .filter((email, index) => index !== action.payload.index),
      };
    case ActionType.UpdateDaysToExpire: {
      let { payload: { daysToExpire } } = action;
      if (daysToExpire < SharedAssetExpiration.Min) {
        daysToExpire = SharedAssetExpiration.Min;
      } else if (daysToExpire > SharedAssetExpiration.Max) {
        daysToExpire = SharedAssetExpiration.Max;
      }
      return {
        ...state,
        daysToExpire,
      };
    }
    case ActionType.ClearAll: {
      const emailsToCache = _.uniq([...state.cachedEmails, ...state.emailAddresses]);
      localStorage.setItem(SHARE_EMAIL_ADDRESSES_STORAGE_KEY, JSON.stringify(emailsToCache));
      return {
        ...state,
        email: INITIAL_STATE.email,
        emailNote: INITIAL_STATE.emailNote,
        emailAddresses: INITIAL_STATE.emailAddresses,
        cachedEmails: emailsToCache,
        daysToExpire: INITIAL_STATE.daysToExpire,
      };
    }
    default:
      return state;
  }
};

export const toggleEmailDialog = (selectedAssets: Array<AssetHit> = []) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ValidationAction.ClearErrors,
    key: ValidationKey.Email,
  });
  dispatch({
    type: ActionType.Toggle,
    payload: {
      selectedAssets,
    },
  });
};

export const updateEmail = (email: string) => ({
  type: ActionType.UpdateEmail,
  payload: {
    email,
  },
});

export const updateEmailNote = (emailNote: string) => ({
  type: ActionType.UpdateEmailNote,
  payload: {
    emailNote,
  },
});

export const updateDaysToExpire = (daysToExpire: number) => ({
  type: ActionType.UpdateDaysToExpire,
  payload: {
    daysToExpire,
  },
});

export const addEmail = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  let { shareEmailDialog: { email } } = state;

  // ignore casing
  email = email.toLowerCase();

  const errors = validationReducer(email, ValidationRules.Email);

  dispatch({
    type: ValidationAction.ToggleErrors,
    key: ValidationKey.Email,
    payload: {
      errors,
    },
  });

  if (!errors.length) {
    dispatch({
      type: ActionType.AddEmail,
      payload: {
        email,
      },
    });
    dispatch({
      type: ActionType.UpdateEmail,
      payload: {
        email: '',
      },
    });
    dispatch({
      type: ValidationAction.ClearErrors,
      key: ValidationKey.Email,
    });
  }
};

export const removeEmail = (index: number) => ({
  type: ActionType.RemoveEmail,
  payload: {
    index,
  },
});

export const clearAll = () => ({
  type: ActionType.ClearAll,
});
